import React, { useMemo, useState } from 'react';
import { BuildingIcon, CheckIcon, KeyIcon, MailIcon, ShieldCheckIcon, UserPlusIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { ScopeSwitcher } from '../../components/admin/ScopeSwitcher';
import { PermissionGate, GrantedVia } from '../../components/admin/PermissionGate';
import { useGovernance } from '../../contexts/GovernanceContext';
import { effectivePermissions } from '../../utils/governance';
import type { DataClass, Scope, StaffMember, Tenant } from '../../types/governance';
const TABS = ['Tenants', 'Staff & access', 'Roles', 'Permissions', 'Approvals', 'Configuration'] as const;
const CLASS_LABEL: Record<DataClass, string> = {
  public: 'Public',
  internal: 'Internal',
  confidential: 'Confidential',
  restricted: 'Restricted',
  highly_restricted: 'Highly restricted'
};
function date(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
export function Governance() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Tenants');
  return <div className="space-y-6" data-id="element-6699" data-mp="17vgkz4fg5nc1">
      <PageHeader eyebrow="Administration" title="Governance" description="Tenants, staff, roles, permissions and approvals. Role, permission and scope are separate throughout — the same role reaches different records for different people." data-id="element-6700" data-mp="17uexg0h8v8df" />
      <Panel title="Operating context" data-id="element-6701" data-mp="k543b1uegc89">
        <ScopeSwitcher data-id="element-6702" data-mp="k41fx3wacrx3" />
      </Panel>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Governance sections" data-id="element-6703" data-mp="1ngjlqsoeycqp" />

      {tab === 'Tenants' && <TenantsTab data-id="element-6704" data-mp="1ihos0vowr3rt" />}
      {tab === 'Staff & access' && <StaffTab data-id="element-6705" data-mp="1igx84i4oauhd" />}
      {tab === 'Roles' && <RolesTab data-id="element-6706" data-mp="1w8tvy9zb6i7y" />}
      {tab === 'Permissions' && <PermissionsTab data-id="element-6707" data-mp="1w7jwbgct51z7" />}
      {tab === 'Approvals' && <ApprovalsTab data-id="element-6708" data-mp="1s78dm9409bqd" />}
      {tab === 'Configuration' && <ConfigurationTab data-id="element-6709" data-mp="1s8jh07tjghgy" />}
    </div>;
}

/* ------------------------------------------------------------------ tenants */

function TenantsTab() {
  const {
    tenants
  } = useGovernance();
  return <div className="space-y-5" data-id="element-6710" data-mp="zjw16rrk6kas">
      <Panel title="Tenant hierarchy" description="Not every tenant uses every level. A single-campus institution is as valid as a multi-campus one." data-id="element-6711" data-mp="zknpdhittfr1">
        <ul className="space-y-5" data-id="element-6712" data-mp="18hplf4c1tnhl">
          {tenants.map(tenant => <li key={tenant.id} data-id="element-6713" data-mp="2owpp54po5rzz">
              <TenantRow tenant={tenant} data-id="element-6714" data-mp="2ovo54aqodv64" />
            </li>)}
        </ul>
      </Panel>
    </div>;
}
function TenantRow({
  tenant
}: {
  tenant: Tenant;
}) {
  return <div className="rounded-card border border-line p-4" data-id="element-6715" data-mp="2sen84q7h91fs">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6716" data-mp="2wgs07kia1q0b">
        <div className="min-w-0" data-id="element-6717" data-mp="2wep20d6z0qki">
          <div className="flex flex-wrap items-center gap-2" data-id="element-6718" data-mp="2ii23i5zddsch">
            <BuildingIcon aria-hidden className="h-5 w-5 shrink-0 text-ink-subtle" data-id="element-6719" data-mp="2ijlkjysuo7fb" />
            <p className="text-body-lg font-semibold text-ink" data-id="element-6720" data-mp="2nig10u3chjw9">{tenant.name}</p>
          </div>
          <p className="mt-1 text-body-sm text-ink-muted" data-id="element-6721" data-mp="3b6pqc5zrops5">
            {tenant.country} · {tenant.currency} · created {date(tenant.createdAt)}
          </p>
        </div>
        <Badge tone={tenant.status === 'active' ? 'ok' : tenant.status === 'suspended' ? 'danger' : tenant.status === 'onboarding' ? 'warn' : 'neutral'} data-id="element-6722" data-mp="31f36xmd9zkeb">
          {tenant.status === 'onboarding' ? 'Onboarding' : tenant.status}
        </Badge>
      </div>

      <ul className="mt-4 space-y-3 border-t border-line pt-3" data-id="element-6723" data-mp="2aaqu054npvra">
        {tenant.institutions.map(institution => <li key={institution.id} data-id="element-6724" data-mp="266j6gb9xqlun">
            <div className="flex flex-wrap items-center gap-2" data-id="element-6725" data-mp="218zrta9bs726">
              <p className="text-body font-medium text-ink" data-id="element-6726" data-mp="219j70j7lchci">{institution.name}</p>
              {institution.verified ? <Badge tone="ok" data-id="element-6727" data-mp="1wc9e2tkztnjx">Verified</Badge> : <Badge tone="warn" data-id="element-6728" data-mp="2tx5ei2jxw64n">Not verified</Badge>}
            </div>
            <p className="mt-0.5 max-w-[70ch] text-body-sm text-ink-muted" data-id="element-6729" data-mp="2ozwtlu7epnzp">
              {institution.verificationSource ? `Source: ${institution.verificationSource}` : 'No verification source recorded. Submission alone never produces verification.'}
            </p>
            <ul className="mt-2 flex flex-wrap gap-2" data-id="element-6730" data-mp="3coy7hwz0drcd">
              {institution.campuses.map(campus => <li key={campus.id} className="rounded-md border border-line bg-canvas px-3 py-2 text-body-sm text-ink" data-id="element-6731" data-mp="37vvcdkel29u2">
                  {campus.name} · {campus.city}
                  <span className="ml-2 text-caption text-ink-subtle" data-id="element-6732" data-mp="3k9ijhmp84rqy">
                    {campus.status.replace(/_/g, ' ')}
                  </span>
                </li>)}
            </ul>
          </li>)}
      </ul>
    </div>;
}

/* -------------------------------------------------------------------- staff */

function StaffTab() {
  const {
    staff,
    roles,
    invitations,
    revokeInvitation,
    reviewAccess,
    revokeAssignment,
    scopeLabel
  } = useGovernance();
  const [inviting, setInviting] = useState(false);
  return <div className="space-y-5" data-id="element-6733" data-mp="gr8ew144bgyo">
      <Panel title="Administrative staff" description="Every staff member is an existing Campuzon identity. No separate administrator account universe exists." action={<div className="flex flex-wrap items-center gap-2" data-id="element-6735" data-mp="ps6kuoy49yjw">
            <GrantedVia permission="staff.assign" data-id="element-6736" data-mp="2mrrmrgd5im10" />
            <Button variant="primary" size="sm" icon={<UserPlusIcon className="h-4 w-4" data-id="element-6738" data-mp="2hurhh7o4lnay" />} onClick={() => setInviting(true)} data-id="element-6737" data-mp="2mq6rfoj0iz23">
              Invite staff
            </Button>
          </div>} bodyClassName="p-0" data-id="element-6734" data-mp="gqfjtxeiufwd">
        <ul className="divide-y divide-line" data-id="element-6739" data-mp="2qrs171092x82">
          {staff.map(member => <li key={member.userId} className="px-5 py-4 sm:px-6" data-id="element-6740" data-mp="35h7qsdo1owm6">
              <StaffRow member={member} onReview={() => reviewAccess(member.userId)} onRevoke={assignmentId => revokeAssignment(member.userId, assignmentId, 'Revoked during administrative access review.')} data-id="element-6741" data-mp="30khc9urw292v" />
            </li>)}
        </ul>
      </Panel>

      <Panel title="Invitations" description="Tokens are single-use and time-limited. An expired invitation grants nothing." bodyClassName="p-0" data-id="element-6742" data-mp="1g5xfziganjuj">
        {invitations.length === 0 ? <div className="px-5 py-6" data-id="element-6743" data-mp="wa48nykxhel8">
            <EmptyState icon={<MailIcon className="h-5 w-5" data-id="element-6745" data-mp="w9uhw8s273eh" />} title="No invitations" description="Nobody is waiting to accept an administrative role." data-id="element-6744" data-mp="w91ixtzcdi6w" />
          </div> : <ul className="divide-y divide-line" data-id="element-6746" data-mp="maltdvfyqgzi">
            {invitations.map(invitation => <li key={invitation.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 py-4 sm:px-6" data-id="element-6747" data-mp="rcb0x13z8ciu">
                <div className="min-w-0" data-id="element-6748" data-mp="ib4lywgz79rp">
                  <p className="text-body font-semibold text-ink" data-id="element-6749" data-mp="ic65lpc254l1">{invitation.name}</p>
                  <p className="text-body-sm text-ink-muted" data-id="element-6750" data-mp="27n6nao3gvu2r">{invitation.email}</p>
                  <p className="mt-1 text-body-sm text-ink" data-id="element-6751" data-mp="2cnkzmic3pq6l">
                    {roles.find(role => role.id === invitation.roleId)?.name} ·{' '}
                    {scopeLabel(invitation.scope)}
                  </p>
                  <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6752" data-mp="3gc1edjltw49y">
                    Invited by {invitation.invitedBy} · expires {date(invitation.expiresAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6753" data-mp="32iafd55q42sh">
                  <Badge tone={invitation.status === 'pending' ? 'warn' : invitation.status === 'accepted' ? 'ok' : 'neutral'} data-id="element-6754" data-mp="32jlg0w3vgufd">
                    {invitation.status}
                  </Badge>
                  {invitation.status === 'pending' && <Button variant="secondary" size="sm" onClick={() => revokeInvitation(invitation.id)} data-id="element-6755" data-mp="mrfjbu2zm5jw">
                      Revoke
                    </Button>}
                </div>
              </li>)}
          </ul>}
      </Panel>

      <InviteDialog open={inviting} onClose={() => setInviting(false)} data-id="element-6756" data-mp="2efdsejqgyt60" />
    </div>;
}
function StaffRow({
  member,
  onReview,
  onRevoke
}: {
  member: StaffMember;
  onReview: () => void;
  onRevoke: (assignmentId: string) => void;
}) {
  const {
    roles,
    scopeLabel
  } = useGovernance();
  const held = effectivePermissions(member, roles);
  return <div data-id="element-6757" data-mp="1uqbth8pyvxod">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6758" data-mp="1upjvxsct7pvg">
        <div className="min-w-0" data-id="element-6759" data-mp="1zq7nqg8v4aqq">
          <p className="text-body-lg font-semibold text-ink" data-id="element-6760" data-mp="1zr9ahhz8qloa">{member.name}</p>
          <p className="cz-num text-body-sm text-ink-muted" data-id="element-6761" data-mp="364qfn34zhaeu">{member.handle}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6762" data-mp="3b41xygl00nyj">
          <Badge tone={member.status === 'active' ? 'ok' : 'danger'} data-id="element-6763" data-mp="23t8g84kld32e">{member.status}</Badge>
          {member.mfaEnabled ? <Badge tone="ok" icon={<ShieldCheckIcon className="h-4 w-4" data-id="element-6765" data-mp="28r0i9reya5kb" />} data-id="element-6764" data-mp="28rjpad32b9jk">
              MFA on
            </Badge> : <Badge tone="warn" data-id="element-6766" data-mp="2djjuol1wiunj">MFA not enabled</Badge>}
        </div>
      </div>

      <dl className="mt-3 grid gap-3 sm:grid-cols-3" data-id="element-6767" data-mp="2ihlfpbiw141n">
        <div data-id="element-6768" data-mp="1kumfwrwwffzj">
          <dt className="text-body-sm text-ink-muted" data-id="element-6769" data-mp="1kv5z0byacaf5">Last login</dt>
          <dd className="text-body text-ink" data-id="element-6770" data-mp="1ptfuyoru3f1t">{date(member.lastLoginAt)}</dd>
        </div>
        <div data-id="element-6771" data-mp="1pvafw333v75w">
          <dt className="text-body-sm text-ink-muted" data-id="element-6772" data-mp="1usk5biqhvitq">Last access review</dt>
          <dd className="text-body text-ink" data-id="element-6773" data-mp="1uun34oxw2g90">
            {member.lastReviewedAt ? date(member.lastReviewedAt) : 'Never reviewed'}
          </dd>
        </div>
        <div data-id="element-6774" data-mp="366nwyi9m0uvm">
          <dt className="text-body-sm text-ink-muted" data-id="element-6775" data-mp="3675z1viye3bg">Effective permissions</dt>
          <dd className="cz-num text-body text-ink" data-id="element-6776" data-mp="3a89fok8ts3dq">{held.length}</dd>
        </div>
      </dl>

      <ul className="mt-3 space-y-2" data-id="element-6777" data-mp="ym7blmwl8d8e">
        {member.assignments.map(assignment => {
        const role = roles.find(item => item.id === assignment.roleId);
        const expired = assignment.expiresAt !== null && new Date(assignment.expiresAt) < new Date();
        return <li key={assignment.id} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 rounded-md border border-line bg-canvas px-3 py-2.5" data-id="element-6778" data-mp="kthm38huoq6j">
              <div className="min-w-0" data-id="element-6779" data-mp="avbjeoo7zg0m">
                <p className="text-body font-medium text-ink" data-id="element-6780" data-mp="ayhbmcs5ay0k">{role?.name ?? 'Unknown role'}</p>
                <p className="text-body-sm text-ink-muted" data-id="element-6781" data-mp="6wvqk45gjdfa">{scopeLabel(assignment.scope)}</p>
                <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6782" data-mp="1xj3biaoc4c6">
                  Assigned by {assignment.assignedBy} · expires{' '}
                  {assignment.expiresAt ? date(assignment.expiresAt) : 'no expiry set'}
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6783" data-mp="2fd9pxdxeia3o">
                {assignment.state === 'awaiting_approval' && <Badge tone="warn" data-id="element-6784" data-mp="2k75mqd1xhqx9">Awaiting a second approver</Badge>}
                {assignment.state === 'revoked' && <Badge tone="neutral" data-id="element-6785" data-mp="2bcqyvm3c182j">Revoked</Badge>}
                {assignment.state === 'active' && expired && <Badge tone="danger" data-id="element-6786" data-mp="2z1s6560fid68">Expired — access stopped</Badge>}
                {assignment.state === 'active' && !expired && <Badge tone="ok" data-id="element-6787" data-mp="333dhuo188leu">Active</Badge>}
                {assignment.state !== 'revoked' && <Button variant="secondary" size="sm" onClick={() => onRevoke(assignment.id)} data-id="element-6788" data-mp="2p3m6kv23hj4j">
                    Revoke
                  </Button>}
              </div>
            </li>;
      })}
      </ul>

      <div className="mt-3" data-id="element-6789" data-mp="1io22u1h4vsxn">
        <PermissionGate permission="staff.assign" label="Marking an access review as complete" data-id="element-6790" data-mp="1ioburulqoa5z">
          <Button variant="ghost" size="sm" onClick={onReview} data-id="element-6791" data-mp="1b3rie3rm37qv">
            Mark access reviewed
          </Button>
        </PermissionGate>
      </div>
    </div>;
}
function InviteDialog({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const {
    roles,
    tenants,
    invite,
    allows
  } = useGovernance();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(roles[0].id);
  const [tenantId, setTenantId] = useState(tenants[1]?.id ?? tenants[0].id);
  const [campusId, setCampusId] = useState('');
  const decision = allows('staff.assign');
  const tenant = tenants.find(item => item.id === tenantId);
  const campuses = tenant?.institutions.flatMap(institution => institution.campuses.map(campus => ({
    campus,
    institutionId: institution.id
  })));
  const role = roles.find(item => item.id === roleId);
  const ready = name.trim() && email.includes('@') && decision.allowed;
  const chosenCampus = campuses?.find(item => item.campus.id === campusId);
  const scope: Scope = chosenCampus ? {
    level: 'campus',
    tenantId,
    institutionId: chosenCampus.institutionId,
    campusId
  } : {
    level: 'tenant',
    tenantId
  };
  return <Modal open={open} onClose={onClose} title="Invite administrative staff" description="A role and a scope, both explicit. Nobody receives access because of a job title." footer={<>
          <Button variant="secondary" onClick={onClose} data-id="element-6793" data-mp="153acci1mf7ds">
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} onClick={() => {
      invite({
        name: name.trim(),
        email: email.trim(),
        roleId,
        scope
      });
      setName('');
      setEmail('');
      onClose();
    }} data-id="element-6794" data-mp="1a2dladkq1xr1">
            Send invitation
          </Button>
        </>} data-id="element-6792" data-mp="raksaj5nkcjb">
      <div className="space-y-5" data-id="element-6795" data-mp="3gtd0p4b4v7s7">
        {!decision.allowed && <p className="rounded-md border border-warn-100 bg-warn-50 px-3 py-2.5 text-body-sm text-ink" data-id="element-6796" data-mp="1xxyqng7uuqrg">
            {decision.message}
          </p>}
        <Field label="Name" htmlFor="inv-name" data-id="element-6797" data-mp="1t55ngws1bqhh">
          <Input id="inv-name" value={name} onChange={event => setName(event.target.value)} data-id="element-6798" data-mp="1p6gdle67gr2l" />
        </Field>
        <Field label="Work email" htmlFor="inv-email" data-id="element-6799" data-mp="1k7libcr1xa1l">
          <Input id="inv-email" type="email" value={email} onChange={event => setEmail(event.target.value)} data-id="element-6800" data-mp="2gwb258m8ocq7" />
        </Field>
        <Field label="Role" htmlFor="inv-role" data-id="element-6801" data-mp="2ct50kea2n9q2">
          <Select id="inv-role" value={roleId} onChange={event => setRoleId(event.target.value)} data-id="element-6802" data-mp="27utqq3js0z7u">
            {roles.map(item => <option key={item.id} value={item.id} data-id="element-6803" data-mp="22yw79od7vcqu">
                {item.name}
                {item.privileged ? ' (needs approval)' : ''}
              </option>)}
          </Select>
        </Field>
        {role?.privileged && <p className="rounded-md border border-warn-100 bg-warn-50 px-3 py-2.5 text-body-sm text-ink" data-id="element-6804" data-mp="syk19l366ycb">
            This is a privileged role. It will not activate on acceptance — a different authorised
            person must approve the assignment first.
          </p>}
        <Field label="Tenant" htmlFor="inv-tenant" data-id="element-6805" data-mp="owyixk7v9ruq">
          <Select id="inv-tenant" value={tenantId} onChange={event => {
          setTenantId(event.target.value);
          setCampusId('');
        }} data-id="element-6806" data-mp="ovnh3pxzeu69">
            {tenants.map(item => <option key={item.id} value={item.id} data-id="element-6807" data-mp="185t85hu8g8k">
                {item.name}
              </option>)}
          </Select>
        </Field>
        <Field label="Campus" htmlFor="inv-campus" hint="Leave unset to grant the whole tenant. Narrower is safer." data-id="element-6808" data-mp="59h93jawtn3k">
          <Select id="inv-campus" value={campusId} onChange={event => setCampusId(event.target.value)} data-id="element-6809" data-mp="3se6w3qrng3j4">
            <option value="" data-id="element-6810" data-mp="38u839y6w0ujm">Whole tenant</option>
            {campuses?.map(({
            campus
          }) => <option key={campus.id} value={campus.id} data-id="element-6811" data-mp="33v5958sg48qf">
                {campus.name}
              </option>)}
          </Select>
        </Field>
      </div>
    </Modal>;
}

/* -------------------------------------------------------------------- roles */

function RolesTab() {
  const {
    roles,
    permissions
  } = useGovernance();
  return <Panel title="Roles" description="A role is a named bundle of permissions and nothing more. There is deliberately no generic “admin” role." bodyClassName="p-0" data-id="element-6812" data-mp="3msoi6cewlrcu">
      <ul className="divide-y divide-line" data-id="element-6813" data-mp="bsgdn1ej9lx">
        {roles.map(role => <li key={role.id} className="px-5 py-4 sm:px-6" data-id="element-6814" data-mp="ec380sajcs85">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6815" data-mp="ecmm1xx2ewgw">
              <div className="min-w-0" data-id="element-6816" data-mp="abt3zl6b0bvx">
                <p className="text-body-lg font-semibold text-ink" data-id="element-6817" data-mp="1gpfz87uxo5yc">{role.name}</p>
                <p className="mt-1 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-6818" data-mp="1grk1ymh3iyx1">{role.description}</p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6819" data-mp="1qnnd2htxe7w4">
                <Badge plain data-id="element-6820" data-mp="1qof0v6ha71bu">{role.domain}</Badge>
                {role.origin === 'custom' && <Badge tone="brand" data-id="element-6821" data-mp="1unxfxlmf8i6r">Custom role</Badge>}
                {role.privileged && <Badge tone="warn" data-id="element-6822" data-mp="xzr9qu9uphqd">Assignment needs approval</Badge>}
              </div>
            </div>
            <ul className="mt-3 flex flex-wrap gap-2" data-id="element-6823" data-mp="12z46c6qiy365">
              {role.permissions.map(permission => {
            const record = permissions.find(item => item.key === permission);
            return <li key={permission} className="cz-num rounded-md border border-line bg-canvas px-2.5 py-1.5 text-caption text-ink" title={record?.label} data-id="element-6824" data-mp="1brxlm45z12k0">
                    {permission}
                  </li>;
          })}
            </ul>
            {role.createdBy && <p className="mt-2 text-caption text-ink-subtle" data-id="element-6825" data-mp="6siykfordm81">Created by {role.createdBy}</p>}
          </li>)}
      </ul>
    </Panel>;
}

/* -------------------------------------------------------------- permissions */

function PermissionsTab() {
  const {
    permissions,
    me,
    roles
  } = useGovernance();
  const held = useMemo(() => new Set(effectivePermissions(me, roles)), [me, roles]);
  const domains = [...new Set(permissions.map(permission => permission.domain))];
  return <div className="space-y-5" data-id="element-6826" data-mp="3f8b1kxq99tu2">
      <Panel title="Permission register" data-id="element-6827" data-mp="3f7j41hd3lm15">
        <p className="max-w-[76ch] text-body-sm text-ink-muted" data-id="element-6828" data-mp="3b67o63k10f65">
          Every action is a permission with a stated data classification. A tick means you hold it
          somewhere — it does not mean you hold it everywhere, which is what scope decides.
        </p>
      </Panel>

      {domains.map(domain => <Panel key={domain} title={domain} bodyClassName="p-0" data-id="element-6829" data-mp="2zzex0lh7p2ex">
          <ul className="divide-y divide-line" data-id="element-6830" data-mp="2v02cw8hq69g1">
            {permissions.filter(permission => permission.domain === domain).map(permission => <li key={permission.key} className="px-5 py-4 sm:px-6" data-id="element-6831" data-mp="2q3243pthqsl8">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6832" data-mp="2l1ctuce3eiy7">
                    <div className="min-w-0" data-id="element-6833" data-mp="2h1u9piu3ibwf">
                      <p className="text-body font-semibold text-ink" data-id="element-6834" data-mp="2c9kky64pvhvq">{permission.label}</p>
                      <p className="cz-num mt-0.5 text-body-sm text-ink-muted" data-id="element-6835" data-mp="27ahh2p0h3hpc">{permission.key}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6836" data-mp="22bwq7g742adk">
                      <Badge plain data-id="element-6837" data-mp="1xcabt3fjqo36">{CLASS_LABEL[permission.dataClass]}</Badge>
                      {permission.requiresApproval && <Badge tone="warn" data-id="element-6838" data-mp="1ta6pfjrc9z34">Needs approval</Badge>}
                      {permission.requiresStepUp && <Badge tone="warn" data-id="element-6839" data-mp="8sxteu04wlzi">Step-up auth</Badge>}
                      {held.has(permission.key) ? <Badge tone="ok" icon={<CheckIcon className="h-4 w-4" data-id="element-6841" data-mp="csy7gdjbmv61" />} data-id="element-6840" data-mp="cthsi349pzoa">
                          You hold this
                        </Badge> : <Badge tone="neutral" icon={<XIcon className="h-4 w-4" data-id="element-6843" data-mp="mre2grlgyokt" />} data-id="element-6842" data-mp="ms6yaxiw3to6">
                          Not held
                        </Badge>}
                    </div>
                  </div>
                  <p className="mt-2 max-w-[76ch] text-body-sm text-ink" data-id="element-6844" data-mp="3q8e00yhqvgub">{permission.note}</p>
                </li>)}
          </ul>
        </Panel>)}
    </div>;
}

/* ---------------------------------------------------------------- approvals */

function ApprovalsTab() {
  const {
    approvals,
    approvalDecision,
    decideApproval,
    scopeLabel
  } = useGovernance();
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const open = approvals.filter(request => request.state === 'requested');
  const decided = approvals.filter(request => request.state !== 'requested');
  return <div className="space-y-5" data-id="element-6845" data-mp="2vfll5u1lkzhn">
      <Panel title="Waiting on a decision" description="Separation of duties is enforced: whoever raised a request can never approve it." bodyClassName="p-0" data-id="element-6846" data-mp="2veahrvc2dtr2">
        {open.length === 0 ? <div className="px-5 py-6" data-id="element-6847" data-mp="2giuk7gc9zfpx">
            <EmptyState icon={<KeyIcon className="h-5 w-5" data-id="element-6849" data-mp="2gl6zflqnfbza" />} title="Nothing waiting" description="No request is currently pending a decision." data-id="element-6848" data-mp="2gjvw1n14868p" />
          </div> : <ul className="divide-y divide-line" data-id="element-6850" data-mp="2qf7b66gfrfgz">
            {open.map(request => {
          const decision = approvalDecision(request);
          const reason = reasons[request.id] ?? '';
          return <li key={request.id} className="px-5 py-4 sm:px-6" data-id="element-6851" data-mp="ryi2tj1bgwc">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6852" data-mp="rq4ezf387k3">
                    <div className="min-w-0" data-id="element-6853" data-mp="1ioa32m92yaya">
                      <p className="text-body-lg font-semibold text-ink" data-id="element-6854" data-mp="1np7iws6maswm">{request.summary}</p>
                      <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-6855" data-mp="19p6l5llng1ld">
                        {request.kind.replace(/_/g, ' ')} · requested by {request.requestedBy} ·{' '}
                        {scopeLabel(request.scope)}
                      </p>
                      <p className="mt-1.5 max-w-[74ch] text-body-sm text-ink" data-id="element-6856" data-mp="22edr35nfntgn">{request.reason}</p>
                    </div>
                    <Badge plain data-id="element-6857" data-mp="26h0bilhryc3t">{request.permissionToApprove}</Badge>
                  </div>

                  {decision.allowed ? <div className="mt-3" data-id="element-6858" data-mp="1xm2eb27lgd6o">
                      <Field label="Decision reason" htmlFor={`apr-${request.id}`} data-id="element-6859" data-mp="1xl11as70pgmb">
                        <Input id={`apr-${request.id}`} value={reason} onChange={event => setReasons(current => ({
                  ...current,
                  [request.id]: event.target.value
                }))} placeholder="Recorded on the audit trail against your name" data-id="element-6860" data-mp="2lavhnjqjpnbb" />
                      </Field>
                      <div className="mt-3 flex flex-wrap gap-2" data-id="element-6861" data-mp="njx7t9a8rqyx">
                        <Button variant="primary" size="sm" disabled={!reason.trim()} onClick={() => decideApproval(request.id, true, reason.trim())} data-id="element-6862" data-mp="1k7brrgf3u61p">
                          Approve
                        </Button>
                        <Button variant="secondary" size="sm" disabled={!reason.trim()} onClick={() => decideApproval(request.id, false, reason.trim())} data-id="element-6863" data-mp="23vkhzkx381yk">
                          Reject
                        </Button>
                      </div>
                    </div> : <p className="mt-3 rounded-md border border-line bg-canvas px-3 py-2.5 text-body-sm text-ink" data-id="element-6864" data-mp="2gdddpfo3negi">
                      {decision.message}
                    </p>}
                </li>;
        })}
          </ul>}
      </Panel>

      {decided.length > 0 && <Panel title="Decided" bodyClassName="p-0" data-id="element-6865" data-mp="2ucwibyhpc7o2">
          <ul className="divide-y divide-line" data-id="element-6866" data-mp="2z567vdewn5pt">
            {decided.map(request => <li key={request.id} className="px-5 py-4 sm:px-6" data-id="element-6867" data-mp="3440yh1j45ykh">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6868" data-mp="3437olrouysy4">
                  <div className="min-w-0" data-id="element-6869" data-mp="38774c82bdnqf">
                    <p className="text-body font-semibold text-ink" data-id="element-6870" data-mp="3d4fmw9rdjts7">{request.summary}</p>
                    <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-6871" data-mp="1d2frzeny4e06">
                      {request.decidedBy} · {date(request.decidedAt)}
                    </p>
                    {request.decisionReason && <p className="mt-1 max-w-[74ch] text-body-sm text-ink" data-id="element-6872" data-mp="1msslvxhnhjiz">
                        {request.decisionReason}
                      </p>}
                  </div>
                  <Badge tone={request.state === 'approved' ? 'ok' : 'neutral'} data-id="element-6873" data-mp="1wq6sz6hoovym">
                    {request.state}
                  </Badge>
                </div>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}

/* ------------------------------------------------------------ configuration */

function ConfigurationTab() {
  const {
    config
  } = useGovernance();
  const [pending, setPending] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const target = config.find(setting => setting.id === pending) ?? null;
  return <div className="space-y-5" data-id="element-6874" data-mp="2i1cmohlma09i">
      <Panel title="Configuration inheritance" data-id="element-6875" data-mp="2i12zt2vvc981">
        <p className="max-w-[76ch] text-body-sm text-ink-muted" data-id="element-6876" data-mp="2i3fl9q0u51pu">
          Effective values flow global → tenant → institution → campus. Where a lower level replaced
          an inherited value, the original is kept beside it so you can always see where a setting
          came from.
        </p>
      </Panel>

      <Panel title="Settings" bodyClassName="p-0" data-id="element-6877" data-mp="1zauymqbqbsuh">
        <ul className="divide-y divide-line" data-id="element-6878" data-mp="1za3fiezbjnl3">
          {config.map(setting => <li key={setting.id} className="px-5 py-4 sm:px-6" data-id="element-6879" data-mp="24azgajoditma">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6880" data-mp="1qa6m601zi0jp">
                <div className="min-w-0" data-id="element-6881" data-mp="1ubs43ztdl50r">
                  <p className="text-body font-semibold text-ink" data-id="element-6882" data-mp="1uctee22khnhf">{setting.label}</p>
                  <p className="mt-0.5 text-body-lg font-semibold text-ink" data-id="element-6883" data-mp="3kny21hawpgnl">{setting.value}</p>
                  <p className="mt-1 text-body-sm text-ink-muted" data-id="element-6884" data-mp="3oqb681a096i9">
                    {setting.overridden ? `Overridden at ${setting.origin} level · inherited value was ${setting.inheritedValue}` : `Inherited from ${setting.origin} configuration`}
                  </p>
                  <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6885" data-mp="22pl51r4xmjt5">
                    Changed by {setting.changedBy} · {date(setting.changedAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-6886" data-mp="1tq845muvj3k1">
                  <Badge plain data-id="element-6887" data-mp="1or4x5wvc2n9f">{setting.domain}</Badge>
                  {setting.overridden ? <Badge tone="brand" data-id="element-6888" data-mp="1jrijxnfi970m">Overridden</Badge> : <Badge plain data-id="element-6889" data-mp="1jtljoz6gmeij">Inherited</Badge>}
                  {setting.requiresApproval && <Badge tone="warn" data-id="element-6890" data-mp="1ev1yx23lr8rr">Change needs approval</Badge>}
                </div>
              </div>
              <p className="mt-2 max-w-[76ch] text-body-sm text-ink" data-id="element-6891" data-mp="396p159otqbi6">{setting.impact}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-6892" data-mp="394vvnxpy5xbr">
                <PermissionGate permission="config.edit" label="Changing this setting" data-id="element-6893" data-mp="3hn7isq68jwkb">
                  <Button variant="secondary" size="sm" onClick={() => setPending(setting.id)} data-id="element-6894" data-mp="3lnzstlmxm5hn">
                    Change value
                  </Button>
                </PermissionGate>
                {!setting.reversible && <span className="text-caption font-medium text-warn-700" data-id="element-6895" data-mp="3vgyjd91zo63w">
                    Not reversible for records already created under the current value
                  </span>}
              </div>
            </li>)}
        </ul>
      </Panel>

      <Modal open={target !== null} onClose={() => setPending(null)} title={target ? `Change ${target.label}` : 'Change setting'} description="Read the impact before confirming. The previous value is recorded, never discarded." footer={<>
            <Button variant="secondary" onClick={() => setPending(null)} data-id="element-6897" data-mp="rwu6p9qpmxoy">
              Cancel
            </Button>
            <Button variant="primary" disabled={!note.trim()} onClick={() => {
        setPending(null);
        setNote('');
      }} data-id="element-6898" data-mp="2tgezeiietfxw">
              {target?.requiresApproval ? 'Submit for approval' : 'Apply change'}
            </Button>
          </>} data-id="element-6896" data-mp="37nylbnc5vzt7">
        {target && <div className="space-y-4" data-id="element-6899" data-mp="29pjpxh46bg2z">
            <dl className="space-y-2" data-id="element-6900" data-mp="29so1x0nvrg47">
              <div data-id="element-6901" data-mp="2epo8rdsk0mwd">
                <dt className="text-body-sm text-ink-muted" data-id="element-6902" data-mp="2eq7g60kkvsw5">What this affects</dt>
                <dd className="text-body text-ink" data-id="element-6903" data-mp="20qeuijnws2v1">{target.impact}</dd>
              </div>
              <div data-id="element-6904" data-mp="25o7x72q06eqk">
                <dt className="text-body-sm text-ink-muted" data-id="element-6905" data-mp="25prgl26yh5wk">Reversible</dt>
                <dd className="text-body text-ink" data-id="element-6906" data-mp="3v34of1ea1c2f">
                  {target.reversible ? 'Yes — the previous value can be restored.' : 'No — records already created under the current value keep it.'}
                </dd>
              </div>
              <div data-id="element-6907" data-mp="926x0av15g5c">
                <dt className="text-body-sm text-ink-muted" data-id="element-6908" data-mp="92zyawb7zdg3">Approval</dt>
                <dd className="text-body text-ink" data-id="element-6909" data-mp="mtll2w3so7j9">
                  {target.requiresApproval ? 'A second authorised person must approve before this takes effect.' : 'Takes effect immediately and is logged.'}
                </dd>
              </div>
            </dl>
            <Field label="Reason for the change" htmlFor="cfg-reason" data-id="element-6910" data-mp="rsyha7gk2qyi">
              <Textarea id="cfg-reason" rows={3} value={note} onChange={event => setNote(event.target.value)} data-id="element-6911" data-mp="rusq4nejysm8" />
            </Field>
          </div>}
      </Modal>
    </div>;
}